<template>
    <side-panel @close="close">
        <template slot="header">
            <h3 v-if="!addressId" class="mt-0 clearfix">
                {{ $l10n.__('Ajouter une adresse') }}
            </h3>
            <h3 v-else class="mt-0 clearfix">
                {{ $l10n.__("Modifier l'adresse") }}
            </h3>
        </template>
        <template slot="content" class="prelative">
            <preloader :display="loading" />
            <div v-if="!loading">
                <p class="gray-60">
                    {{
                        $l10n.__("Définissez un nom pour votre adresse et ainsi mieux l'identifier")
                    }}
                </p>
                <el-form
                    ref="addressForm"
                    label-position="top"
                    :model="address"
                    :rules="rules"
                    label-width="120px"
                >
                    <el-form-item
                        :label="$t.__('common.labels.address_name')"
                        prop="label"
                        :error="errors.get('label')"
                    >
                        <text-input
                            :mode="mode"
                            v-model="address.label"
                            :placeholder="$t.__('common.labels.address_name')"
                        />
                    </el-form-item>
                    <el-form-item :label="$t.__('common.labels.address')">
                        <address-input v-model="address"></address-input>
                    </el-form-item>
                </el-form>
            </div>
        </template>

        <template slot="footer">
            <CompassButton
                :label="$t.__('common.actions.cancel')"
                type="secondary"
                @click.prevent="close"
            />
            <CompassButton
                :label="$t.__('common.actions.save')"
                class="ml-3"
                @click.prevent="submitForm('addressForm')"
            />
        </template>
    </side-panel>
</template>

<script>
import AddressRules from './rules/AddressRules';
import FormMixins from '../../mixins/FormMixins';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import TextInput from 'vcomponents/inputs/TextInput.vue';
import SidePanel from 'vcomponents/utilities/SidePanel.vue';
import Address from 'vcomponents/inputs/custom/Address.vue';

export default {
    mixins: [FormMixins],
    props: {
        addressId: {
            type: [Number, Boolean],
            required: false,
            default: false,
        },
        linkedid: {
            type: Number,
            required: true,
            default: 0,
        },
        linkedtype: {
            type: String,
            required: true,
            default: '',
        },
    },
    data() {
        return {
            loading: false,
            mode: 'edit',
            rules: AddressRules,
            formatedCity: '',
            cityByAlgolia: true,
            addressInputTemplate: {
                value: (suggestion) => {
                    return suggestion.name;
                },
            },
            address: {
                label: this.$l10n.__('Adresse principale'),
                manualMode: false,
            },
        };
    },

    created() {
        if (this.addressId) {
            this.getAddress();
        }
    },

    methods: {
        getAddress() {
            this.loading = true;

            this.$ajax.get(
                `/rest/addresses/${this.linkedtype}/${this.linkedid}/${this.addressId}`,
                {},
                (response) => {
                    this.address = this.hydrateAddress(response);
                    this.formatedCity = [response.town, response.state, response.country]
                        .filter((item) => !!item)
                        .join(', ');
                    this.loading = false;
                },
                (error) => {
                    this.close();
                    Push.error(this.$l10n.__("Problème lors de la récupération de l'addresse"));
                    this.loading = false;
                },
            );
        },

        save() {
            this.loading = true;
            if (this.addressId) {
                this.$ajax.put(
                    `/rest/addresses/${this.linkedtype}/${this.linkedid}/${this.addressId}`,
                    {},
                    this.persistAddress(this.address),
                    (response) => {
                        EventBus.$emit('edit-address', response);
                        Push.success(this.$l10n.__('Votre adresse a été mise à jour'));
                        this.close();
                    },
                    (error) => {
                        this.formatAndDisplayErrors(error.response.data.error.message);
                        Push.error(
                            this.$l10n.__(
                                "Une erreur est survenue lors de la modification de l'adresse",
                            ),
                        );
                    },
                    () => {
                        this.loading = false;
                    },
                );
            } else {
                this.$ajax.post(
                    `/rest/addresses/${this.linkedtype}/${this.linkedid}`,
                    {},
                    this.persistAddress(this.address),
                    (response) => {
                        EventBus.$emit('add-address', response);
                        Push.success(this.$l10n.__('Votre adresse a été créée'));
                        this.close();
                    },
                    (error) => {
                        this.formatAndDisplayErrors(error.response.data.error.message);
                        Push.error(
                            this.$l10n.__(
                                "Une erreur est survenue lors de l'enregistrement de l'adresse",
                            ),
                        );
                        this.loading = false;
                    },
                );
            }
        },

        close() {
            this.$emit('close', this.address);
        },

        formatAndDisplayErrors(messages) {
            let fieldMap = {
                country: 'town',
                countrycode: 'town',
            };

            let messageError = {};
            if (messages && Object.keys(messages).length > 0) {
                Object.keys(messages).map((index, message) => {
                    if (fieldMap[index]) {
                        let key = fieldMap[index];
                        messageError[key] = messages[index];
                    }
                });
            }
            this.errors.setErrors(messageError);
        },
        persistAddress(data) {
            return {
                name: data.label,
                part1: data.line1 || data.name,
                part2: data.line2 || '',
                part3: data.line3 || '',
                part4: data.line4 || '',
                lat: data.latlng?.lat,
                lng: data.latlng?.lng,
                zip: data.postcode || data.postalCode,
                state: data.county,
                town: data.city,
                countrycode: data.countryCode?.toUpperCase(),
                country: data.country,
                linkedid: this.linkedid,
                linkedtype: this.linkedtype,
            };
        },
        hydrateAddress(data) {
            return {
                manualMode: true,
                label: data.name,
                name: data.name,
                line1: data.part1,
                line2: data.part2,
                line3: data.part3,
                line4: data.part4,
                latLng: {
                    lat: data.lat,
                    lng: data.lng,
                },
                postalCode: data.zip,
                county: data.state,
                city: data.town,
                countryCode: data.countrycode,
                country: data.country,
                linkedid: this.linkedid,
                linkedtype: this.linkedtype,
            };
        },
    },

    components: {
        'text-input': TextInput,
        Preloader,
        SidePanel,
        'address-input': Address,
    },
};
</script>
